import type { Cadeia } from '@core/tipos';
import type { EstadoLetra } from '@core/termo/avaliacao';
import { baixarArquivo } from './baixar';
import type { Enquadramento, PaletaImagem } from './desenho';
import { celulasEmOrdem, enquadrar, pintarCelula, pintarFundo, pintarLegenda } from './desenho';

/**
 * O tabuleiro inteiro como imagem parada (§7.5).
 *
 * Desenhar no canvas a partir da cadeia, e não fotografar o DOM: o tabuleiro
 * em tela é virtualizado (§16) e recortado pela câmera, então uma foto traria
 * só o pedaço visível. Aqui a cadeia é a fonte, e o que sai é a partida
 * completa, independente de onde a câmera estava e de quanto zoom havia.
 *
 * As cores e as medidas vêm de `desenho`, as mesmas que o GIF usa: os dois
 * formatos têm de produzir a mesma imagem, e uma segunda cópia divergiria no
 * primeiro ajuste.
 */

/** Escala de desenho: o dobro, para a imagem não sair borrada em tela retina. */
const ESCALA = 2;

export interface OpcoesImagem {
  readonly cadeia: Cadeia;
  /** §23: marcas do dia por palavra. Ausente fora da Trama. */
  readonly marcas?: ReadonlyMap<string, readonly EstadoLetra[]> | undefined;
  /** Linha discreta no rodapé — o nome do jogo e o do modo. */
  readonly legenda: string;
  readonly paleta: PaletaImagem;
}

/**
 * Desenha a cadeia inteira e devolve o canvas.
 *
 * A ordem de pintura repete a da tela: a marca do dia fica por cima da ponte
 * que cruza a palavra, e não o contrário (§23.3).
 */
export function desenharTabuleiro(opcoes: OpcoesImagem): HTMLCanvasElement {
  const { cadeia, marcas, paleta } = opcoes;

  const enq: Enquadramento = enquadrar(cadeia);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(enq.largura * ESCALA);
  canvas.height = Math.round(enq.altura * ESCALA);

  const ctx = canvas.getContext('2d');
  if (ctx === null) return canvas;

  ctx.scale(ESCALA, ESCALA);
  pintarFundo(ctx, enq, paleta);

  // As marcadas por último: a ponte nasce um turno depois da base que atravessa
  // e apagaria justamente a célula do cruzamento, onde o amarelo mais cai.
  const celulas = celulasEmOrdem(cadeia, marcas, enq, paleta);
  const marcadas = new Set(celulas.filter((c) => c.marcada).map((c) => c.chave));

  for (const celula of celulas) {
    // Célula sem marca em lugar de marcada nem chega a ser pintada: seria
    // coberta na passada seguinte de qualquer jeito.
    if (!celula.marcada && !marcadas.has(celula.chave)) pintarCelula(ctx, celula, paleta);
  }
  for (const celula of celulas) {
    if (celula.marcada) pintarCelula(ctx, celula, paleta);
  }

  pintarLegenda(ctx, enq, opcoes.legenda, paleta);
  return canvas;
}

/** Entrega o PNG ao navegador. */
export function baixarCanvas(canvas: HTMLCanvasElement, nome: string): Promise<boolean> {
  return new Promise((resolver) => {
    canvas.toBlob((blob) => {
      if (blob === null) {
        resolver(false);
        return;
      }
      baixarArquivo(blob, nome);
      resolver(true);
    }, 'image/png');
  });
}
